import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  FormControl, 
  InputLabel, 
  MenuItem, 
  Select,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Chip
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import Autocomplete from '@mui/material/Autocomplete';

import axios from 'axios';
import baseURL from '../../baseURL';
import GirviPageShell from '../common/GirviPageShell';
import { PledgePhotosRow } from '../common/PhotoThumb';
import { fingerprintService } from '../../services/fingerprintService';
import { printDocument } from '../../utils/printUtility';
import ReleasePrint from '../print/templates/ReleasePrint';
import { compressImageFileUnder10Kb } from '../../utils/compressImageUnder10Kb';
import {
  computeInterest,
  days360,
  resolvePledgeDhara,
  yearlyCompoundRelease
} from '../../utils/dharaInterest';
import { DEFAULT_DHARA, formatDhara } from '../../constants/dhara';

function toDateInputValue(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function ReleasePhotoPick({ label, file, onFile }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [preview, setPreview] = useState('');
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraError, setCameraError] = useState('');

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraOn(false);
  };

  useEffect(() => {
    if (!file) {
      setPreview('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!cameraOn || !videoRef.current || !streamRef.current) return undefined;
    videoRef.current.srcObject = streamRef.current;
    videoRef.current.play().catch(() => {});
    return undefined;
  }, [cameraOn]);

  useEffect(() => () => stopCamera(), []);

  const startCamera = async () => {
    setCameraError('');
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError('Camera is not available in this browser.');
        return;
      }
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      streamRef.current = stream;
      setCameraOn(true);
    } catch (err) {
      setCameraError('Allow the camera, or use Add.');
    }
  };

  const takePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const shot = new File([blob], `${label.toLowerCase()}-capture.jpg`, { type: 'image/jpeg' });
      onFile(shot);
      stopCamera();
    }, 'image/jpeg', 0.92);
  };

  const btnSx = {
    flex: 1,
    height: 30,
    textTransform: 'none',
    fontWeight: 800,
    fontSize: 12,
    borderRadius: '8px',
    boxShadow: 'none',
  };

  return (
    <Box
      sx={{
        flex: '1 1 0',
        minWidth: 0,
        border: '1px solid #e2e8f0',
        borderRadius: '14px',
        bgcolor: '#fff',
        p: 1,
        boxShadow: '0 8px 20px rgba(15,23,42,0.05)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#1e3a5f' }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#c9a24d', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Optional
        </Typography>
      </Box>
      <Box
        sx={{
          height: 168,
          borderRadius: '10px',
          overflow: 'hidden',
          bgcolor: '#0b1220',
          border: '1px solid rgba(201,162,77,0.35)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {cameraOn ? (
          <Box component="video" ref={videoRef} autoPlay playsInline muted sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : preview ? (
          <Box component="img" src={preview} alt={label} sx={{ width: '100%', height: '100%', objectFit: 'contain', bgcolor: '#0b1220' }} />
        ) : (
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#f5e6c8', textAlign: 'center', px: 1 }}>
            Add a file or capture with the camera
          </Typography>
        )}
      </Box>
      {cameraError ? (
        <Typography sx={{ mt: 0.5, fontSize: 11, color: '#b91c1c' }}>{cameraError}</Typography>
      ) : null}
      <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75 }}>
        {cameraOn ? (
          <>
            <Button variant="contained" onClick={takePhoto} sx={{ ...btnSx, background: 'linear-gradient(180deg, #1e3a5f, #0b1220)' }}>
              Take photo
            </Button>
            <Button variant="outlined" onClick={stopCamera} sx={{ ...btnSx, borderColor: '#cbd5e1', color: '#334155' }}>
              Close
            </Button>
          </>
        ) : (
          <>
            <Button variant="outlined" component="label" sx={{ ...btnSx, borderColor: '#c9a24d', color: '#1e3a5f' }}>
              Add
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  stopCamera();
                  onFile(e.target.files?.[0] || null);
                }}
              />
            </Button>
            <Button variant="contained" onClick={startCamera} sx={{ ...btnSx, background: 'linear-gradient(180deg, #1e3a5f, #0b1220)' }}>
              Capture
            </Button>
            {file ? (
              <Button variant="text" onClick={() => onFile(null)} sx={{ ...btnSx, flex: '0 0 auto', minWidth: 36, color: '#b91c1c' }}>
                Clear
              </Button>
            ) : null}
          </>
        )}
      </Box>
    </Box>
  );
}

const ReleasePage = ({ initialTicket = '', embedded = false }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editReleaseId = searchParams.get('edit');
  const ticketFromUrl = searchParams.get('ticket') || '';
  const [isEditMode, setIsEditMode] = useState(false);
  const [ticketNumber, setTicketNumber] = useState('');
  const [pledgeData, setPledgeData] = useState(null);
  const [releaseDate, setReleaseDate] = useState(new Date());
  const [days, setDays] = useState(0);
  const [error, setError] = useState('');
  const [adjustedPrincipal, setAdjustedPrincipal] = useState(0);
  const [receiptAmount, setReceiptAmount] = useState(0);
  const [showConfirmBox, setShowConfirmBox] = useState(false);
  const [continueInput, setContinueInput] = useState('');
  const [searchType, setSearchType] = useState('firstName');
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showReleasePhotoDialog, setShowReleasePhotoDialog] = useState(false);
  const [pendingPaymentForRelease, setPendingPaymentForRelease] = useState(null);
  const [releaseCustomerPhotoDataUrl, setReleaseCustomerPhotoDataUrl] = useState(null);
  const [releaseArticlePhotoDataUrl, setReleaseArticlePhotoDataUrl] = useState(null);
  const [compressingReleasePhotos, setCompressingReleasePhotos] = useState(false);
  const [lastReleasePrintPayload, setLastReleasePrintPayload] = useState(null);
  const [releasePhotoCustFile, setReleasePhotoCustFile] = useState(null);
  const [releasePhotoArtFile, setReleasePhotoArtFile] = useState(null);
  const [releaseData, setReleaseData] = useState(null);
  const [isVerifyingFingerprint, setIsVerifyingFingerprint] = useState(false);
  const [fingerprintVerified, setFingerprintVerified] = useState(false);
  const [fingerprintScore, setFingerprintScore] = useState(null);
  const [verifiedFingerprintTemplate, setVerifiedFingerprintTemplate] = useState(null);
  const [releaseInterestMode, setReleaseInterestMode] = useState('compound');

  // Payment mode dialog states
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentDialogError, setPaymentDialogError] = useState('');
  const [paymentRows, setPaymentRows] = useState([{ type: 'cash', bankLedgerCode: '', bankLedgerName: '', amount: '' }]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);

  const fetchBankAccounts = async () => {
    try {
      setLoadingBanks(true);
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${baseURL}/ledgers/bank-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBankAccounts(resp?.data?.data || []);
    } catch (err) {
      console.error('Error fetching bank accounts:', err);
    } finally {
      setLoadingBanks(false);
    }
  };

  // Helper function to format date as dd/mm/yyyy
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Calculate total received interest
  const totalReceivedInterest = () => {
    if (!pledgeData?.receipts) return 0;
    return pledgeData.receipts.reduce((total, receipt) => {
      const interestDetails = receipt?.interestDetails || {};
      return total + (interestDetails.calculatedInterest || 0);
    }, 0);
  };

  // Ensure body scroll is restored when entering Release (avoids stuck state from other screens)
  useEffect(() => {
    document.body.style.overflow = 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  // Load release data for edit mode
  useEffect(() => {
    const fetchReleaseForEdit = async () => {
      if (editReleaseId) {
        try {
          setIsEditMode(true);
          const token = localStorage.getItem('token');
          const response = await fetch(`${baseURL}/releases/${editReleaseId}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            const release = data.release || data.data || data;
            
            if (release) {
              // Set release date
              if (release.releaseDate) {
                setReleaseDate(new Date(release.releaseDate));
              }
              
              // Set ticket number and fetch pledge
              if (release.ticketNo) {
                setTicketNumber(release.ticketNo);
                await fetchPledgeData(release.ticketNo);
              } else if (release.pledgeId?.loanDetails?.ticketNo) {
                setTicketNumber(release.pledgeId.loanDetails.ticketNo);
                await fetchPledgeData(release.pledgeId.loanDetails.ticketNo);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching release for edit:', error);
        }
      }
    };

    fetchReleaseForEdit();
  }, [editReleaseId]);

  const fetchPledgeData = async (ticketNo) => {
    try {
      const token = localStorage.getItem('token');
      // Use search API with ticket number instead of direct pledge fetch
      const response = await axios.get(`${baseURL}/pledges/search`, {
        params: {
          searchType: 'ticketNo',
          searchValue: ticketNo
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
  
      // Get the first result (should be only one match)
      const pledge = response.data.data.pledges[0];
      console.log('Fetched Pledge Data:', pledge);
      if (pledge) {
        setPledgeData(pledge);
        const openedDays = days360(pledge.date, new Date());
        setReleaseInterestMode(openedDays > 360 ? 'compound' : 'simple');
        setFingerprintVerified(false);
        setFingerprintScore(null);
        setVerifiedFingerprintTemplate(null);
        setContinueInput('');
        setReleasePhotoCustFile(null);
        setReleasePhotoArtFile(null);
        setError('');
      } else {
        setError('Pledge not found');
        setPledgeData(null);
      }
    } catch (err) {
      console.error('Error fetching pledge:', err);
      setError(err.response?.data?.message || 'Error fetching pledge data');
      setPledgeData(null);
    }
  };

  useEffect(() => {
    const t = initialTicket || ticketFromUrl;
    if (t) fetchPledgeData(t);
  }, [initialTicket, ticketFromUrl]);

  const releaseFigures = () => {
    const principal = Number(pledgeData?.loanDetails?.principalAmount) || 0;
    const rate = Number(pledgeData?.loanDetails?.rateOfInterest) || 0;
    const pledgeDate = pledgeData?.date ? new Date(pledgeData.date) : null;
    const releaseDateObj = releaseDate ? new Date(releaseDate) : null;
    const actualDays = pledgeDate && releaseDateObj ? days360(pledgeDate, releaseDateObj) : 0;
    const overYear = actualDays > 360;
    const useCompound = overYear && releaseInterestMode === 'compound';
    const compound = useCompound ? yearlyCompoundRelease(principal, rate, actualDays) : null;
    if (compound) {
      return { overYear, principal, totalInt: compound.interest, advance: 0, balance: compound.total, actualDays, mode: 'compound' };
    }
    const dhara = resolvePledgeDhara(pledgeData, DEFAULT_DHARA);
    const totalInt = actualDays > 0 ? computeInterest(principal, rate, actualDays, dhara).interest : 0;
    const advance = Math.round(((principal * rate) / 100 / 12) * 100) / 100;
    const balance = Math.round((principal + totalInt - advance) * 100) / 100;
    return { overYear, principal, totalInt, advance, balance, actualDays, mode: 'simple' };
  };

  const calculateInterest = () => releaseFigures().totalInt;

  const releaseBreakdown = () => {
    const figures = releaseFigures();
    return {
      principal: figures.principal,
      totalInt: figures.totalInt,
      advance: figures.advance,
      balance: figures.balance,
    };
  };

  const calculateTotalAmount = () => {
    if (!pledgeData || !days) return '0.00';
    const interest = calculateInterest();
    const totalAmount =
      (pledgeData?.loanDetails?.principalAmount || 0) + parseFloat(interest);

    return totalAmount.toFixed(2);
  };
  const searchTimeout = useRef(null);
  const handleSearch = async (value, type = null) => {
    try {
      setError('');
      setSearchLoading(true);
      
      // If type is provided, update searchType and searchValue
      if (type) {
        setSearchType(type);
        setSearchValue(value);
      }
  
      if (!value.trim()) {
        setError('Please enter a search value');
        setSearchLoading(false);
        return;
      }


  
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseURL}/pledges/search`, {
        params: {
          searchType: type || searchType,
          searchValue: value.trim()
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
  
      setSearchResults(response.data.data.pledges || []);
      setSearchLoading(false);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.response?.data?.message || 'Error searching pledges');
      setSearchLoading(false);
    }
  };

  const handleFingerprintSearch = async () => {
    try {
      setError('');
      setSearchLoading(true);
      const result = await fingerprintService.searchPledgesByScan(60);
      const matches = result?.matches || [];

      if (matches.length > 0) {
        const customerCode = matches[0]?.customer?.code;
        if (customerCode) {
          const token = localStorage.getItem('token');
          const resp = await axios.get(`${baseURL}/pledges/client/${customerCode}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const allPledges = resp?.data?.pledges || [];
          const activePledges = allPledges.filter(
            p => p.status !== 'RELEASED' && !p.loanDetails?.released
          );
          setSearchResults(activePledges);
          if (activePledges.length === 0) {
            setError('Fingerprint matched but no active pledges found for this customer.');
          }
        } else {
          setSearchResults(matches);
        }
      } else {
        setSearchResults([]);
        setError('No pledges matched this fingerprint.');
      }
    } catch (err) {
      console.error('Fingerprint search error:', err);
      setError(err.message || 'Error searching pledges by fingerprint');
    } finally {
      setSearchLoading(false);
    }
  };
  const handleTicketNumberChange = (e) => {
    setTicketNumber(e.target.value);
  };

  const handleCompleteTransaction = async (paymentModes = [], custPhotoUrl, artPhotoUrl) => {
    try {
      setLoading(true);
      setError('');
      
      if (!pledgeData || !pledgeData?.loanDetails || !pledgeData?.loanDetails?.ticketNo) {
        setError('Invalid pledge data. Please search for the pledge again.');
        return;
      }
      if (!pledgeData?._id) {
        setError('Pledge ID missing. Please search the pledge again.');
        return;
      }

      const customerPhotoData = custPhotoUrl ?? releaseCustomerPhotoDataUrl;
      const articlePhotoData = artPhotoUrl ?? releaseArticlePhotoDataUrl;

      const principal = Number(pledgeData?.loanDetails?.principalAmount) || 0;
      const interest = calculateInterest() || 0;
      const totalAmount = principal + interest;
      
      const validPayments = paymentModes.filter(p => p.amount && Number(p.amount) > 0).map(p => ({
        type: p.type,
        bankLedgerCode: p.bankLedgerCode || null,
        bankLedgerName: p.bankLedgerName || null,
        amount: Number(p.amount)
      }));

      const releasePayload = {
        ticketNo: pledgeData?.loanDetails?.ticketNo,
        releaseDate: releaseDate ? releaseDate.toISOString() : new Date().toISOString(),
        principal: principal,
        interest: interest,
        total: totalAmount,
        interestMode: releaseFigures().mode,
        totalPaid: 0,
        totalInterestPaid: 0,
        totalPrincipalPaid: 0,
        paymentModes: validPayments,
        releaseCustomerPhotoData: customerPhotoData,
        releaseArticlePhotoData: articlePhotoData,
        ...(verifiedFingerprintTemplate
          ? { fingerprintTemplate: verifiedFingerprintTemplate, fingerprintThreshold: 60 }
          : {})
      };

      console.log('Sending release payload:', {
        ...releasePayload,
        releaseCustomerPhotoData: releasePayload.releaseCustomerPhotoData ? '[base64]' : null,
        releaseArticlePhotoData: releasePayload.releaseArticlePhotoData ? '[base64]' : null
      });
      console.log('Pledge Data:', pledgeData);

      // Save release to backend using fetch instead of axios
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/releases`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(releasePayload)
      });

      const responseData = await response.json();
      console.log('Response Data:', responseData);

      if (!response.ok) {
        console.error('Response Error Details:', responseData);
        throw new Error(responseData.message || responseData.details || `HTTP error! status: ${response.status}`);
      }

      if (responseData.success) {
        setReleaseData(responseData.data);
        setLastReleasePrintPayload({
          pledgeData,
          releaseData: {
            ...(responseData.data?.release || responseData.data),
            paymentModes: validPayments
          }
        });
        setReleaseCustomerPhotoDataUrl(null);
        setReleaseArticlePhotoDataUrl(null);
        setPendingPaymentForRelease(null);
        setShowPrintModal(true);
      } else {
        throw new Error(responseData.message || 'Failed to save release');
      }
      
    } catch (error) {
      console.error('Error completing transaction:', error);
      setError(error.message || 'Failed to complete transaction. Please try again.');
    } finally {
      setIsVerifyingFingerprint(false);
      setLoading(false);
    }
  };

  const handleScanAndMatchFingerprint = async () => {
    try {
      setError('');
      if (!pledgeData?._id) {
        setError('Please load a pledge first.');
        return;
      }
      if (!pledgeData?.fingerprint?.template) {
        setError('No enrolled fingerprint found for this pledge.');
        return;
      }

      setIsVerifyingFingerprint(true);
      const captured = await fingerprintService.capture();
      const verification = await fingerprintService.verifyWithPledge(pledgeData._id, captured.template);
      if (!verification?.verified) {
        setFingerprintVerified(false);
        setFingerprintScore(Number(verification?.score ?? 0));
        setVerifiedFingerprintTemplate(null);
        throw new Error(`Fingerprint not matched${verification?.score ? ` (score: ${verification.score})` : ''}`);
      }

      setFingerprintVerified(true);
      setFingerprintScore(Number(verification.score ?? 0));
      setVerifiedFingerprintTemplate(captured.template);
    } catch (error) {
      console.error('Error scanning/verifying fingerprint:', error);
      setError(error.message || 'Fingerprint scan/verification failed');
    } finally {
      setIsVerifyingFingerprint(false);
    }
  };

  const handlePrint = async () => {
    try {
      if (lastReleasePrintPayload?.pledgeData && lastReleasePrintPayload?.releaseData) {
        await printDocument('release', lastReleasePrintPayload, ReleasePrint);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Print failed');
    }
    window.location.reload();
  };

  const handleCloseModal = () => {
    setShowPrintModal(false);
    window.location.reload(); // Reload the page when closing the modal
  };

  const handleReleaseDateChange = (date) => {
    if (!date) {
      setReleaseDate(null);
      setDays(0);
      return;
    }

    const releaseDate = new Date(date);
    if (isNaN(releaseDate.getTime())) {
      setError('Invalid date selected');
      return;
    }

    setReleaseDate(releaseDate);
    if (pledgeData) {
      const pledgeDate = new Date(pledgeData.date);
      if (isNaN(pledgeDate.getTime())) {
        setError('Invalid pledge date');
        return;
      }

      const actualDays = days360(pledgeDate, releaseDate);
      const figuresDays = (() => {
        const principal = Number(pledgeData?.loanDetails?.principalAmount) || 0;
        const rate = Number(pledgeData?.loanDetails?.rateOfInterest) || 0;
        const overYear = actualDays > 360;
        const useCompound = overYear && releaseInterestMode === 'compound';
        if (useCompound) return actualDays;
        const dhara = resolvePledgeDhara(pledgeData, DEFAULT_DHARA);
        return computeInterest(principal, rate, actualDays, dhara).chargedDays;
      })();
      setDays(figuresDays);
    }
  };

  const openPaymentDialog = () => {
    const { balance } = releaseBreakdown();
    setPaymentDialogError('');
    setPaymentRows([{
      type: 'cash',
      bankLedgerCode: '',
      bankLedgerName: '',
      amount: balance > 0 ? String(balance) : ''
    }]);
    setShowPaymentDialog(true);
    fetchBankAccounts();
  };

  const handleInterestTypeChange = (e) => {
    if (e.target.value.toUpperCase() === 'Y') {
      openPaymentDialog();
    } else if (e.target.value.toUpperCase() === 'N') {
      setContinueInput('');
    }
  };

  const handleKeyPress = (e) => {
    if ((e.key || '').toLowerCase() === 'y') {
      setShowPrintModal(true);
    } else if ((e.key || '').toLowerCase() === 'n') {
      // Handle cancel action
      navigate('/release');
    }
  };

  const rateLabel = pledgeData ? `${pledgeData?.loanDetails?.rateOfInterest || 0}%` : '';

  const sectionTitleSx = {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#1e3a5f',
    mb: 0.75,
    pb: 0.5,
    borderBottom: '1px solid rgba(201,162,77,0.35)',
  };
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      height: 32,
      borderRadius: '8px',
      fontSize: 13,
      fontWeight: 600,
      bgcolor: '#fff',
    },
  };
  const dialogPaperSx = {
    borderRadius: '16px',
    overflow: 'hidden',
    border: '1px solid rgba(212,175,106,0.28)',
  };
  const dialogTitleSx = {
    background: 'linear-gradient(180deg, #0b1220, #1a2d4a)',
    color: '#f5e6c8',
    fontSize: 15,
    fontWeight: 750,
    py: 1.15,
    px: 2,
  };
  const primaryBtnSx = {
    textTransform: 'none',
    fontWeight: 800,
    borderRadius: '8px',
    background: 'linear-gradient(180deg, #1e3a5f, #0b1220)',
    border: '1px solid rgba(201,162,77,0.45)',
    boxShadow: 'none',
    '&:hover': { background: 'linear-gradient(180deg, #2a4d78, #122038)', boxShadow: 'none' },
  };

  const PrintModal = () => (
    <Dialog
      open={showPrintModal}
      onClose={handleCloseModal}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={dialogTitleSx}>Release successful</DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        <DialogContentText sx={{ fontSize: 13, color: '#334155', m: 0 }}>
          The release is saved. Print the receipt now. For the A5 layout, set Master, Printer Settings, Release receipt, then print again.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
        <Button onClick={handleCloseModal} sx={{ textTransform: 'none', fontWeight: 700, color: '#334155' }}>
          Close
        </Button>
        <Button variant="contained" onClick={handlePrint} startIcon={<PrintIcon />} sx={primaryBtnSx}>
          Print receipt
        </Button>
      </DialogActions>
    </Dialog>
  );

  const cust = pledgeData?.customer || {};
  const custName = [
    cust.firstName,
    cust.relation && cust.relationName ? `${String(cust.relation).toUpperCase()} ${cust.relationName}` : '',
    cust.lastName,
  ].filter(Boolean).join(' ') || '—';

  return (
    <>
      <Box sx={embedded ? { maxHeight: '78vh', overflow: 'auto' } : { height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <GirviPageShell
          title="Create Release"
          subtitle="Pledge · Release"
          chipLabel="Pledge"
          maxWidth="xl"
          compact
          fitViewport={!embedded}
          bare={embedded}
          disableNavbar={embedded}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {!embedded && <><Box sx={{ border: '1px solid #e2e8f0', borderRadius: '10px', bgcolor: '#fff', p: 1 }}>
              <Typography sx={sectionTitleSx}>Find pledge</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '160px 1fr auto 150px auto' }, gap: 0.75, alignItems: 'center' }}>
                <FormControl size="small" fullWidth sx={fieldSx}>
                  <InputLabel>Search by</InputLabel>
                  <Select
                    value={searchType}
                    label="Search by"
                    onChange={(e) => {
                      setSearchType(e.target.value);
                      setSearchValue('');
                      setSearchResults([]);
                    }}
                  >
                    <MenuItem value="firstName">Customer name</MenuItem>
                    <MenuItem value="lastName">Last name</MenuItem>
                    <MenuItem value="customerCode">Customer code</MenuItem>
                    <MenuItem value="village">Village</MenuItem>
                    <MenuItem value="date">Date</MenuItem>
                    <MenuItem value="mobile">Mobile</MenuItem>
                    <MenuItem value="ticketNo">Ticket number</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  fullWidth
                  value={searchValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchValue(value);
                    handleSearch(value);
                  }}
                  placeholder={searchType === 'date' ? 'YYYY-MM-DD' : searchType === 'ticketNo' ? 'Ticket number' : 'Type to search'}
                  error={!!error && !pledgeData}
                  sx={fieldSx}
                  InputProps={{ endAdornment: searchLoading ? <CircularProgress size={16} /> : null }}
                />
                <Button
                  variant="outlined"
                  onClick={handleFingerprintSearch}
                  disabled={searchLoading}
                  sx={{ height: 32, textTransform: 'none', fontWeight: 700, borderRadius: '8px', whiteSpace: 'nowrap', borderColor: '#cbd5e1', color: '#1e3a5f' }}
                >
                  Scan finger
                </Button>
                <TextField
                  size="small"
                  label="Ticket"
                  value={ticketNumber}
                  onChange={handleTicketNumberChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') fetchPledgeData(ticketNumber);
                  }}
                  sx={fieldSx}
                />
                <Button variant="contained" onClick={() => fetchPledgeData(ticketNumber)} sx={{ ...primaryBtnSx, height: 32, px: 1.5 }}>
                  Load
                </Button>
              </Box>
            </Box>

            {searchResults.length > 0 && (
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '10px', bgcolor: '#fff', overflow: 'auto', maxHeight: 168 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {['Date', 'Ticket', 'Name', 'Mobile', 'Village', 'Principal', 'Status'].map((h) => (
                        <TableCell key={h} sx={{ py: 0.5, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#64748b', bgcolor: '#f8fafc' }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchResults.map((pledge) => (
                      <TableRow
                        key={pledge._id}
                        hover
                        onClick={() => {
                          handleSearch(pledge.loanDetails.ticketNo, 'ticketNo');
                          setTicketNumber(pledge.loanDetails.ticketNo);
                          fetchPledgeData(pledge.loanDetails.ticketNo);
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ py: 0.4, fontSize: 12 }}>{formatDate(pledge.date)}</TableCell>
                        <TableCell sx={{ py: 0.4, fontSize: 12, fontWeight: 800, color: '#1e3a5f' }}>{pledge.loanDetails.ticketNo}</TableCell>
                        <TableCell sx={{ py: 0.4, fontSize: 12 }}>{`${pledge.customer.firstName || ''} ${pledge.customer.lastName || ''}`}</TableCell>
                        <TableCell sx={{ py: 0.4, fontSize: 12 }}>{pledge.customer.mobileNo}</TableCell>
                        <TableCell sx={{ py: 0.4, fontSize: 12 }}>{pledge.customer.village}</TableCell>
                        <TableCell sx={{ py: 0.4, fontSize: 12 }} align="right">₹{pledge.loanDetails.principalAmount?.toLocaleString()}</TableCell>
                        <TableCell sx={{ py: 0.4 }}>
                          <Chip
                            size="small"
                            label={pledge.loanDetails.released ? 'Released' : 'Active'}
                            sx={{
                              height: 20,
                              fontSize: 11,
                              fontWeight: 700,
                              bgcolor: pledge.loanDetails.released ? '#fee2e2' : '#dcfce7',
                              color: pledge.loanDetails.released ? '#b91c1c' : '#166534',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {searchResults.length === 0 && searchValue && (
              <Alert severity="info" sx={{ py: 0, borderRadius: '8px' }}>No pledges found.</Alert>
            )}
            </>}

            {error && (
              <Alert severity="error" sx={{ py: 0, borderRadius: '8px' }}>{error}</Alert>
            )}

            {pledgeData && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(260px, 0.85fr) 1.3fr' }, gap: 1, alignItems: 'start' }}>
                <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '10px', bgcolor: 'linear-gradient(180deg, #f8fafc, #fff)', background: 'linear-gradient(180deg, #f8fafc, #fff)', p: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ ...sectionTitleSx, flex: 1, mb: 0 }}>Pledge</Typography>
                    {days > 0 && (
                      <Chip size="small" label={`${days} days`} sx={{ height: 22, fontWeight: 800, bgcolor: '#fdebd0', color: '#d35400' }} />
                    )}
                  </Box>
                  <Typography sx={{ mt: 0.75, fontSize: 15, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>{custName}</Typography>
                  <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.25 }}>
                    {[cust.mobileNo, cust.village].filter(Boolean).join(' · ') || '—'}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, mt: 1 }}>
                    {[
                      ['Ticket', pledgeData?.loanDetails?.ticketNo || 'N/A'],
                      ['Principal', `₹${(pledgeData?.loanDetails?.principalAmount || 0).toFixed(2)}`],
                      ['Rate', rateLabel],
                      ['Pledge date', formatDate(pledgeData?.date)],
                      ['Code', cust.code || 'N/A'],
                      ['Duration', `${pledgeData?.loanDetails?.duration || 'N/A'} months`],
                      ['Dhara', formatDhara(resolvePledgeDhara(pledgeData, DEFAULT_DHARA)) || 'N/A'],
                      ['Village', cust.village || 'N/A'],
                    ].map(([k, v]) => (
                      <Box key={k} sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', px: 0.75, py: 0.4 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8' }}>{k}</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 750, color: '#0f172a' }}>{v}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <PledgePhotosRow clientPhoto={pledgeData?.clientPhoto} articlePhoto={pledgeData?.articlePhoto} size={44} />
                  </Box>
                  <Typography sx={{ ...sectionTitleSx, mt: 1.25, mb: 0.5 }}>Articles</Typography>
                  {(pledgeData.articles || []).length ? (pledgeData.articles || []).map((article, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.35, borderTop: '1px solid #e2e8f0' }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>{article.description || 'Article'}</Typography>
                      <Typography sx={{ fontSize: 12, color: '#475569', whiteSpace: 'nowrap' }}>
                        ₹{Number(article.valueTotal || 0).toLocaleString('en-IN')} · {Number(article.grossWeight || article.grossWt || 0).toFixed(3)} g
                      </Typography>
                    </Box>
                  )) : (
                    <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>No articles</Typography>
                  )}
                </Box>

                {releaseDate && (() => {
                  const { principal, totalInt, advance, balance } = releaseBreakdown();
                  const rows = [
                    { label: 'Principal amount', value: principal, minus: false, bold: false },
                    { label: 'Total interest', value: totalInt, minus: false, bold: false },
                    ...(advance > 0
                      ? [{ label: 'Advance interest', value: advance, minus: true, bold: false }]
                      : []),
                    { label: 'Balance', value: balance, minus: false, bold: true },
                  ];
                  return (
                    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '10px', bgcolor: '#fff', p: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                      <Typography sx={{ ...sectionTitleSx, mb: 0, pb: 0, borderBottom: 'none' }}>Release amount</Typography>
                      {releaseFigures().overYear && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {[
                            { id: 'compound', label: 'Compound int' },
                            { id: 'simple', label: 'Simple int' },
                          ].map((option) => {
                            const active = releaseInterestMode === option.id;
                            return (
                              <Box
                                key={option.id}
                                onClick={() => setReleaseInterestMode(option.id)}
                                sx={{
                                  cursor: 'pointer',
                                  px: 1,
                                  py: 0.35,
                                  borderRadius: 1,
                                  fontSize: 12,
                                  fontWeight: 750,
                                  border: '1px solid',
                                  borderColor: active ? '#0f172a' : '#dbe2ea',
                                  bgcolor: active ? '#0f172a' : '#fff',
                                  color: active ? '#fff' : '#475569',
                                }}
                              >
                                {option.label}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                      <TextField
                        type="date"
                        size="small"
                        label="Release date"
                        value={toDateInputValue(releaseDate)}
                        onChange={(e) => {
                          const parts = String(e.target.value || '').split('-').map(Number);
                          if (parts.length !== 3 || parts.some((n) => !n)) return;
                          handleReleaseDateChange(new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0));
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ ...fieldSx, width: 168 }}
                      />
                    </Box>
                      <Table size="small">
                        <TableBody>
                          {rows.map((row) => (
                            <TableRow key={row.label}>
                              <TableCell sx={{ py: 0.55, fontSize: 13, fontWeight: row.bold ? 800 : 600, borderColor: '#e2e8f0', bgcolor: row.bold ? '#fdebd0' : 'transparent', borderBottom: row.bold ? 'none' : undefined }}>
                                {row.label}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 0.55, fontSize: 13, fontWeight: row.bold ? 800 : 700, color: row.minus ? '#b91c1c' : '#0f172a', borderColor: '#e2e8f0', bgcolor: row.bold ? '#fdebd0' : 'transparent', borderBottom: row.bold ? 'none' : undefined }}>
                                {row.minus ? `−₹${row.value.toFixed(2)}` : `₹${row.value.toFixed(2)}`}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  );
                })()}
              </Box>
            )}

            {pledgeData && releaseDate && (
              <Box
                sx={{
                  mt: 'auto',
                  p: 0.75,
                  borderRadius: '12px',
                  background: 'linear-gradient(180deg, rgba(11,18,32,0.96), rgba(22,35,58,0.96))',
                  border: '1px solid rgba(212,175,106,0.28)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleScanAndMatchFingerprint}
                  disabled={loading || isVerifyingFingerprint || !pledgeData}
                  sx={{ height: 32, textTransform: 'none', fontWeight: 700, color: '#f5e6c8', borderColor: 'rgba(245,230,200,0.4)', borderRadius: '8px' }}
                >
                  {isVerifyingFingerprint ? 'Scanning...' : 'Scan fingerprint'}
                </Button>
                {fingerprintVerified && (
                  <Typography sx={{ color: '#86efac', fontSize: 12, fontWeight: 700 }}>
                    Matched{fingerprintScore !== null ? ` (${fingerprintScore})` : ''}
                  </Typography>
                )}
                <Typography sx={{ color: 'rgba(245,230,200,0.8)', fontSize: 12, fontWeight: 700, ml: { md: 'auto' } }}>
                  Y to release
                </Typography>
                <TextField
                  size="small"
                  value={continueInput}
                  placeholder="Y / N"
                  disabled={loading || isVerifyingFingerprint}
                  onChange={(e) => {
                    const input = e.target.value;
                    setContinueInput(input);
                    if (input.toUpperCase() === 'Y') openPaymentDialog();
                    else if (input.toUpperCase() === 'N') setContinueInput('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.preventDefault();
                  }}
                  sx={{ width: 72, '& .MuiInputBase-root': { height: 32, bgcolor: '#fff', borderRadius: '8px', fontWeight: 800 } }}
                />
              </Box>
            )}
          </Box>
        </GirviPageShell>
      </Box>

      <Dialog open={showPaymentDialog} onClose={() => setShowPaymentDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={dialogTitleSx}>Payment — release</DialogTitle>
        <DialogContent sx={{ pt: '14px !important', bgcolor: '#f8fafc' }}>
          {pledgeData && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
              {[
                ['Ticket', pledgeData?.loanDetails?.ticketNo],
                ['Balance', `₹${releaseBreakdown().balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`],
              ].map(([k, v]) => (
                <Chip key={k} size="small" label={`${k}: ${v}`} sx={{ fontWeight: 700, bgcolor: '#fff', border: '1px solid #e2e8f0' }} />
              ))}
              {(() => {
                const totalEntered = paymentRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
                const totalDue = releaseBreakdown().balance;
                const remaining = Math.round((totalDue - totalEntered) * 100) / 100;
                return (
                  <Chip
                    size="small"
                    label={remaining > 0.02 ? `Pending to loss: ₹${remaining.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : remaining < -0.02 ? `Over by ₹${Math.abs(remaining).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : 'Paid in full'}
                    sx={{
                      fontWeight: 800,
                      bgcolor: Math.abs(remaining) <= 0.02 ? '#dcfce7' : remaining > 0 ? '#fee2e2' : '#fee2e2',
                      color: Math.abs(remaining) <= 0.02 ? '#166534' : '#b91c1c',
                    }}
                  />
                );
              })()}
            </Box>
          )}
          <Table size="small" sx={{ bgcolor: '#fff', borderRadius: '10px', overflow: 'hidden', tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 140, py: 0.5, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', color: '#64748b' }}>Type</TableCell>
                <TableCell sx={{ py: 0.5, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', color: '#64748b' }}>Bank account</TableCell>
                <TableCell sx={{ width: 150, py: 0.5, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', color: '#64748b' }}>Amount</TableCell>
                <TableCell sx={{ width: 84 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentRows.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell sx={{ py: 0.4 }}>
                    <Select
                      fullWidth
                      size="small"
                      value={row.type}
                      onChange={(e) => {
                        const updated = [...paymentRows];
                        updated[idx].type = e.target.value;
                        if (e.target.value === 'cash') {
                          updated[idx].bankLedgerCode = '';
                          updated[idx].bankLedgerName = '';
                        }
                        setPaymentRows(updated);
                      }}
                      sx={{ height: 32, borderRadius: '8px', fontSize: 13 }}
                    >
                      <MenuItem value="cash">Cash</MenuItem>
                      <MenuItem value="upi">UPI</MenuItem>
                      <MenuItem value="bank_transfer">Bank transfer</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell sx={{ py: 0.4 }}>
                    {row.type !== 'cash' ? (
                      <Autocomplete
                        size="small"
                        options={bankAccounts}
                        getOptionLabel={(opt) => [opt.firstName, opt.lastName].filter(Boolean).join(' ') || opt.code || ''}
                        value={bankAccounts.find((b) => b.code === row.bankLedgerCode) || null}
                        onChange={(e, val) => {
                          const updated = [...paymentRows];
                          updated[idx].bankLedgerCode = val?.code || '';
                          updated[idx].bankLedgerName = val ? [val.firstName, val.lastName].filter(Boolean).join(' ') : '';
                          setPaymentRows(updated);
                        }}
                        loading={loadingBanks}
                        renderInput={(params) => <TextField {...params} placeholder="Bank account" sx={fieldSx} />}
                      />
                    ) : (
                      <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 0.4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={row.amount}
                      onChange={(e) => {
                        const updated = [...paymentRows];
                        updated[idx].amount = e.target.value;
                        setPaymentRows(updated);
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={fieldSx}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.4, whiteSpace: 'nowrap' }}>
                    {paymentRows.length > 1 && (
                      <IconButton size="small" color="error" onClick={() => setPaymentRows(paymentRows.filter((_, i) => i !== idx))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                    {idx === paymentRows.length - 1 && (
                      <IconButton
                        size="small"
                        onClick={() => setPaymentRows([...paymentRows, { type: 'cash', bankLedgerCode: '', bankLedgerName: '', amount: '' }])}
                        sx={{ color: '#15803d', bgcolor: '#dcfce7', width: 28, height: 28, ml: 0.25 }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {paymentDialogError ? (
            <Alert severity="error" sx={{ mt: 1, py: 0, borderRadius: '8px' }}>{paymentDialogError}</Alert>
          ) : null}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setPaymentRows([...paymentRows, { type: 'cash', bankLedgerCode: '', bankLedgerName: '', amount: '' }])}
            sx={{ mt: 1, textTransform: 'none', fontWeight: 800, color: '#166534' }}
          >
            Add payment
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.25, bgcolor: '#fff' }}>
          <Button onClick={() => setShowPaymentDialog(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={loading}
            sx={primaryBtnSx}
            onClick={() => {
              const { balance } = releaseBreakdown();
              const validPayments = paymentRows
                .filter((p) => p.amount && Number(p.amount) > 0)
                .map((p) => ({
                  type: p.type,
                  bankLedgerCode: p.bankLedgerCode || null,
                  bankLedgerName: p.bankLedgerName || null,
                  amount: Number(p.amount),
                }));
              const totalEntered = validPayments.reduce((s, r) => s + (Number(r.amount) || 0), 0);
              if (totalEntered - balance > 0.02) {
                setPaymentDialogError(`Payment ₹${totalEntered.toFixed(2)} is more than the balance ₹${balance.toFixed(2)}.`);
                return;
              }
              setPaymentDialogError('');
              setError('');
              setReleasePhotoCustFile(null);
              setReleasePhotoArtFile(null);
              setPendingPaymentForRelease(validPayments);
              setShowPaymentDialog(false);
              setShowReleasePhotoDialog(true);
            }}
          >
            Continue to photos
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showReleasePhotoDialog}
        onClose={() => {
          if (compressingReleasePhotos || loading) return;
          setShowReleasePhotoDialog(false);
          setPendingPaymentForRelease(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={dialogTitleSx}>Release photos</DialogTitle>
        <DialogContent sx={{ pt: '14px !important', bgcolor: '#f8fafc' }}>
          <Typography sx={{ fontSize: 12, color: '#64748b', mb: 1 }}>
            Optional. Use Add for a file, or Capture to take a photo now.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <ReleasePhotoPick label="Customer" file={releasePhotoCustFile} onFile={setReleasePhotoCustFile} />
            <ReleasePhotoPick label="Article" file={releasePhotoArtFile} onFile={setReleasePhotoArtFile} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.25 }}>
          <Button
            disabled={compressingReleasePhotos || loading}
            onClick={() => {
              setShowReleasePhotoDialog(false);
              setPendingPaymentForRelease(null);
            }}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={compressingReleasePhotos || loading}
            sx={primaryBtnSx}
            onClick={async () => {
              try {
                setCompressingReleasePhotos(true);
                setError('');
                let customerPhoto = null;
                let articlePhoto = null;
                if (releasePhotoCustFile) customerPhoto = await compressImageFileUnder10Kb(releasePhotoCustFile);
                if (releasePhotoArtFile) articlePhoto = await compressImageFileUnder10Kb(releasePhotoArtFile);
                setReleaseCustomerPhotoDataUrl(customerPhoto);
                setReleaseArticlePhotoDataUrl(articlePhoto);
                setShowReleasePhotoDialog(false);
                await handleCompleteTransaction(pendingPaymentForRelease || [], customerPhoto, articlePhoto);
              } catch (err) {
                console.error(err);
                setError(err.message || 'Could not compress photos');
              } finally {
                setCompressingReleasePhotos(false);
              }
            }}
          >
            {compressingReleasePhotos ? 'Compressing…' : 'Save and release'}
          </Button>
        </DialogActions>
      </Dialog>

      <PrintModal />
    </>
  );
};

export default ReleasePage;